import Matter from 'matter-js';

import BodiesFactory from './BodiesFactory';
import { Game } from '@combos-fun/engine';
import type { PhysicsSystemParams } from './PhysicsSystem';
import type { Physics } from './Physics';
import { MatterPixiDebugRenderer } from './MatterPixiDebugRenderer';

export interface PhysicsLinkedBody extends Matter.Body {
  component: Physics;
}

/** Collision pairs often report a compound part, not the parent body. */
function resolveLinkedComponent(body: Matter.Body): Physics | undefined {
  let current: Matter.Body | undefined = body;
  while (current) {
    const linked = current as PhysicsLinkedBody;
    if (linked.component) {
      return linked.component;
    }
    if (!current.parent || current.parent === current) {
      break;
    }
    current = current.parent;
  }
  return undefined;
}

export default class PhysicsEngine {
  private Engine: typeof Matter.Engine;
  private World: typeof Matter.World;
  private engine: Matter.Engine;
  private bodiesFactory: BodiesFactory;
  private collisionEvents: string[];
  private bodyEvents: string[];
  private options: PhysicsSystemParams;
  private game: Game;
  private Runner: typeof Matter.Runner;
  private Constraint: typeof Matter.Constraint;
  private mouseConstraint: Matter.MouseConstraint;
  private runner: Matter.Runner;
  private debugRenderer: MatterPixiDebugRenderer | null = null;
  public enabled: boolean = false;
  constructor(game: Game, options: PhysicsSystemParams) {
    this.Engine = Matter.Engine;
    this.World = Matter.World;
    this.bodiesFactory = new BodiesFactory();
    this.Runner = Matter.Runner;
    this.Constraint = Matter.Constraint;
    this.game = game;

    this.collisionEvents = ['collisionStart', 'collisionActive', 'collisionEnd'];
    this.bodyEvents = ['tick', 'beforeUpdate', 'afterUpdate', 'beforeRender', 'afterRender', 'afterTick'];
    this.options = options;
    this.runner = this.Runner.create({
      delta: 1000 / (this.options.fps || 60),
      frameDeltaSmoothing: (this.options.deltaSampleSize ?? 1) > 1,
    });
  }

  public start() {
    this.engine = this.Engine.create();
    const world = this.World.create(this.options.world as Matter.IWorldDefinition);
    this.engine.world = world;
    if (this.options.isTest) {
      const ownCanvas = !this.options.canvas;
      const canvas = this.options.canvas ?? document.createElement('canvas');
      if (ownCanvas && this.options.element) {
        this.options.element.appendChild(canvas);
      }
      const resolution = this.options.resolution || 1;
      this.debugRenderer = new MatterPixiDebugRenderer({
        engine: this.engine,
        canvas,
        width: this.game.canvas.width / resolution,
        height: this.game.canvas.height / resolution,
        resolution,
      });
      void this.debugRenderer.init().catch(err => {
        console.error('[plugin-matterjs] MatterPixiDebugRenderer init failed', err);
      });
      this.Runner.run(this.runner, this.engine);
    }
    this.enabled = true;
    this.initMouse();
    this.initCollisionEvents();
    this.initBodyEvents();
  }

  public update(e) {
    if (!this.engine) {
      return;
    }
    if (!this.options.isTest) {
      this.Runner.tick(this.runner, this.engine, e.currentTime);
    }
  }

  public stop() {
    this.enabled = false;
    this.runner.enabled = false;
  }

  public destroy() {
    this.debugRenderer?.destroy();
    this.debugRenderer = null;
    if (this.engine) {
      Matter.Runner.stop(this.runner);
      Matter.Engine.clear(this.engine);
    }
  }

  public awake() {
    this.enabled = true;
    this.runner.enabled = true;
  }
  public add(component) {
    const body = this.createBodies(component);
    this.World.add(this.engine.world, [body]);
    this.linkComponent(component, body);
  }

  public change(component: Physics) {
    const newBody = this.createBodies(component);
    this.World.remove(this.engine.world, component.body, true);
    this.World.add(this.engine.world, [newBody]);
    this.linkComponent(component, newBody);
  }
  public remove(component: Physics) {
    this.World.remove(this.engine.world, component.body, true);
    component.body = undefined;
  }

  private createBodies(params): PhysicsLinkedBody {
    const body = this.bodiesFactory.create(params) as PhysicsLinkedBody;
    if (!body) {
      throw new Error('[plugin-matterjs] Failed to create Matter body');
    }
    return body;
  }

  private linkComponent(component, body: PhysicsLinkedBody) {
    component.body = body;
    component.Body = Matter.Body;
    component.PhysicsEngine = this.engine;
    component.Constraint = this.Constraint;
    component.mouseConstraint = this.mouseConstraint;
    component.World = this.World;
    body.component = component;
  }

  private initCollisionEvents() {
    (this.collisionEvents as Array<'collisionStart' | 'collisionActive' | 'collisionEnd'>).forEach(eventName => {
      Matter.Events.on(this.engine, eventName, (event: Matter.IEventCollision<Matter.Engine>) => {
        const pairs = event.pairs ?? [];
        for (let i = 0; i < pairs.length; i++) {
          const pair = pairs[i];
          const { bodyA, bodyB } = pair;
          const componentA = resolveLinkedComponent(bodyA);
          const componentB = resolveLinkedComponent(bodyB);
          if (!componentA || !componentB) {
            continue;
          }
          componentA.emit(eventName, componentB.gameObject, componentA.gameObject);
          componentB.emit(eventName, componentA.gameObject, componentB.gameObject);
        }
      });
    });
  }

  private initMouse() {
    if (this.options.mouse && this.options.mouse.open) {
      const mouse = Matter.Mouse.create(this.game.canvas);
      let options = this.options.mouse.constraint ? {
        mouse,
        constraint: this.options.mouse.constraint
      } : {
          mouse
        };
      this.mouseConstraint = Matter.MouseConstraint.create(this.engine, options);
      this.World.add(this.engine.world, this.mouseConstraint);
    }
  }

  private initBodyEvents() {
    this.bodyEvents.forEach(eventName => {
      Matter.Events.on(this.engine, eventName, e => {
        const bodies = e.source.world.bodies;
        bodies.forEach(body => {
          const linked = body as PhysicsLinkedBody;
          linked.component?.emit(eventName, body, linked.component.gameObject);
        });
      });
    });
  }
}
